// src/components/common/audio-player/session/useOSMediaSession.ts

"use client";

import { useEffect } from "react";
import { audioSession } from "./AudioSession";

type Props = {
  title?: string;
  artist?: string;
  artwork?: string;
};

export function useOSMediaSession({ title, artist, artwork }: Props) {
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    if (!("mediaSession" in navigator)) return;

    const mediaSession = navigator.mediaSession;

    mediaSession.metadata = new MediaMetadata({
      title: title || "TownLIT",
      artist: artist || "",
      album: "TownLIT",
      artwork: artwork
        ? [
            { src: artwork, sizes: "96x96", type: "image/png" },
            { src: artwork, sizes: "128x128", type: "image/png" },
            { src: artwork, sizes: "192x192", type: "image/png" },
            { src: artwork, sizes: "256x256", type: "image/png" },
            { src: artwork, sizes: "384x384", type: "image/png" },
            { src: artwork, sizes: "512x512", type: "image/png" },
          ]
        : [],
    });

    const syncState = () => {
      const a = audioSession.getAudio();

      try {
        mediaSession.playbackState = a && !a.paused ? "playing" : "paused";
      } catch {}

      try {
        const setPositionState = (mediaSession as any).setPositionState?.bind(mediaSession);
        if (
          setPositionState &&
          a &&
          Number.isFinite(a.duration) &&
          a.duration > 0
        ) {
          setPositionState({
            duration: a.duration,
            playbackRate: a.playbackRate || 1,
            position: Math.min(a.currentTime || 0, a.duration),
          });
        }
      } catch {}
    };

    mediaSession.setActionHandler("play", () => audioSession.play());
    mediaSession.setActionHandler("pause", () => audioSession.pause());
    mediaSession.setActionHandler("stop", () => audioSession.pause());

    mediaSession.setActionHandler("seekbackward", () => {
      const a = audioSession.getAudio();
      if (!a) return;
      audioSession.seek((a.currentTime || 0) - 5);
    });

    mediaSession.setActionHandler("seekforward", () => {
      const a = audioSession.getAudio();
      if (!a) return;
      audioSession.seek((a.currentTime || 0) + 5);
    });

    mediaSession.setActionHandler("seekto", (details: any) => {
      const a = audioSession.getAudio();
      if (!a) return;

      if (details?.fastSeek && "fastSeek" in HTMLMediaElement.prototype) {
        (a as any).fastSeek(details.seekTime);
        return;
      }

      audioSession.seek(details.seekTime);
    });

    const unsubscribe = audioSession.subscribe(syncState);
    syncState();

    return () => {
      unsubscribe();

      try {
        mediaSession.setActionHandler("play", null);
        mediaSession.setActionHandler("pause", null);
        mediaSession.setActionHandler("stop", null);
        mediaSession.setActionHandler("seekbackward", null);
        mediaSession.setActionHandler("seekforward", null);
        mediaSession.setActionHandler("seekto", null);
      } catch {}
    };
  }, [title, artist, artwork]);
}